import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminApi from "../api/adminApi";
import { toast } from "react-toastify";

export default function BulkProductUpload() {
  const navigate = useNavigate();

  const [csv, setCsv] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [imagePreviews, setImagePreviews] = useState([]);

  /* ================= IMAGE PREVIEWS ================= */

  useEffect(() => {
    if (!images.length) {
      setImagePreviews([]);
      return;
    }

    const previews = Array.from(images).map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
    }));

    setImagePreviews(previews);

    return () => {
      previews.forEach((preview) => {
        URL.revokeObjectURL(preview.url);
      });
    };
  }, [images]);

  /* ================= SUBMIT ================= */

  const submit = async (e) => {
    e.preventDefault();

    if (!csv) {
      toast.error("Please select a CSV file");
      return;
    }

    if (images.length === 0) {
      toast.error("Please select product images");
      return;
    }

    const formData = new FormData();

    formData.append("csv", csv);

    for (let i = 0; i < images.length; i++) {
      formData.append("images", images[i]);
    }

    /* ================= DEBUG ================= */

    console.log("===== DEBUG UPLOAD =====");
    console.log("CSV:", csv?.name);
    console.log("Images Count:", images.length);

    for (const pair of formData.entries()) {
      console.log(
        "FormData:",
        pair[0],
        pair[1]?.name || pair[1]
      );
    }

    try {
      setLoading(true);

      const res = await adminApi.post(
        "admin/bulk-products/bulk-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("✅ RESPONSE:", res.data);

      toast.success(
        `Successfully uploaded ${
          res.data.count || 0
        } products`
      );

      // Reset
      setCsv(null);
      setImages([]);

      // Reset file inputs
      const fileInputs =
        document.querySelectorAll(
          'input[type="file"]'
        );

      fileInputs.forEach((input) => {
        input.value = "";
      });

      // Redirect after success
      setTimeout(() => {
        navigate("/products");
      }, 1500);
    } catch (err) {
      console.error("❌ UPLOAD ERROR:", err);

      if (err.response) {
        console.error(
          "SERVER ERROR:",
          err.response.data
        );

        toast.error(
          err.response.data?.message ||
            "Upload failed. Please check CSV and images."
        );
      } else {
        toast.error(
          "Network error. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  /* ================= CSV SELECT ================= */

  const handleCsvChange = (e) => {
    const file = e.target.files?.[0];

    console.log("CSV Selected:", file?.name);

    if (!file) {
      setCsv(null);
      return;
    }

    if (
      !file.name.toLowerCase().endsWith(".csv")
    ) {
      toast.error("Please select a CSV file");
      e.target.value = "";
      setCsv(null);
      return;
    }

    setCsv(file);
  };

  /* ================= IMAGE SELECT ================= */

  const handleImagesChange = (e) => {
    const files = e.target.files;

    if (!files || files.length === 0) {
      setImages([]);
      return;
    }

    const selectedImages = Array.from(files);

    console.log(
      "Images Selected:",
      selectedImages.map((file) => file.name)
    );

    setImages(selectedImages);
  };

  /* ================= REMOVE IMAGE ================= */

  const removeImage = (index) => {
    setImages((currentImages) =>
      currentImages.filter((_, i) => i !== index)
    );
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* ================= HEADER ================= */}

      <div className="mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
          Bulk Product Upload
        </h1>

        <p className="text-sm text-gray-500 mt-1">
          Upload a CSV file and product images to
          add multiple products at once.
        </p>
      </div>

      {/* ================= MAIN CARD ================= */}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <form
          onSubmit={submit}
          className="space-y-6"
        >
          {/* ================= CSV ================= */}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Product CSV
            </label>

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-6 text-center hover:border-blue-400 transition">
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleCsvChange}
                className="hidden"
              />

              <label
                htmlFor="csv-upload"
                className="cursor-pointer block"
              >
                <div className="text-3xl mb-2">
                  📄
                </div>

                <p className="text-sm font-medium text-gray-700">
                  {csv
                    ? csv.name
                    : "Choose CSV file"}
                </p>

                <p className="text-xs text-gray-500 mt-1">
                  CSV files only
                </p>

                <span className="inline-block mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700">
                  Browse CSV
                </span>
              </label>
            </div>

            {csv && (
              <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <p className="text-xs text-green-700 font-medium">
                    CSV selected
                  </p>

                  <p className="text-sm text-green-800 truncate">
                    {csv.name}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setCsv(null);

                    const input =
                      document.getElementById(
                        "csv-upload"
                      );

                    if (input) {
                      input.value = "";
                    }
                  }}
                  className="text-red-500 hover:text-red-700 text-sm font-medium ml-3"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* ================= IMAGES ================= */}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Product Images
            </label>

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-6 text-center hover:border-blue-400 transition">
              <input
                id="images-upload"
                type="file"
                multiple
                accept="image/*"
                onChange={handleImagesChange}
                className="hidden"
              />

              <label
                htmlFor="images-upload"
                className="cursor-pointer block"
              >
                <div className="text-3xl mb-2">
                  🖼️
                </div>

                <p className="text-sm font-medium text-gray-700">
                  Select product images
                </p>

                <p className="text-xs text-gray-500 mt-1">
                  You can select multiple images
                </p>

                <span className="inline-block mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700">
                  Browse Images
                </span>
              </label>
            </div>
          </div>

          {/* ================= IMAGE PREVIEW ================= */}

          {images.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Selected Images
                </h3>

                <span className="text-xs text-gray-500">
                  {images.length}{" "}
                  {images.length === 1
                    ? "image"
                    : "images"}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {imagePreviews.map(
                  (preview, index) => (
                    <div
                      key={`${preview.name}-${index}`}
                      className="relative group"
                    >
                      <img
                        src={preview.url}
                        alt={preview.name}
                        className="w-full h-28 sm:h-32 object-cover rounded-lg border border-gray-200"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          removeImage(index)
                        }
                        className="absolute top-1.5 right-1.5 w-7 h-7 bg-red-600 text-white rounded-full text-sm opacity-90 hover:opacity-100"
                        title="Remove image"
                      >
                        ×
                      </button>

                      <p className="text-[11px] text-gray-500 mt-1 truncate">
                        {preview.name}
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* ================= INFO ================= */}

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 sm:p-4">
            <p className="text-sm font-medium text-blue-800">
              Upload requirements
            </p>

            <ul className="mt-2 text-xs sm:text-sm text-blue-700 space-y-1">
              <li>
                • Upload a valid CSV containing
                your product data.
              </li>

              <li>
                • Select all product images
                required by the CSV.
              </li>

              <li>
                • Make sure image names match
                the names referenced in your CSV.
              </li>
            </ul>
          </div>

          {/* ================= ACTIONS ================= */}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() =>
                navigate("/products")
              }
              disabled={loading}
              className="w-full sm:w-auto px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={
                loading ||
                !csv ||
                images.length === 0
              }
              className="w-full sm:flex-1 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? "Uploading..."
                : "Upload Products"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}